import {
  Sparkles,
  Mail,
  Reply,
  ReplyAll,
  Forward,
  Trash2,
  Star,
  Paperclip,
  Loader2,
  Plus,
  X,
  Languages,
  Wand2,
  Send,
  Flag,
  CheckCircle2,
} from 'lucide-react';
import { useState, useMemo, useRef, useEffect, type KeyboardEvent } from 'react';
import { format } from 'date-fns';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMailStore } from '../../store/mail';
import { useMessage } from '../../hooks/useMessages';
import { useTags } from '../../hooks/useTags';
import { useAccounts } from '../../hooks/useAccounts';
import { useAddMessageTag, useRemoveMessageTag } from '../../hooks/useMessageTags';
import { useBadges, useAddMessageBadge, useRemoveMessageBadge } from '../../hooks/useBadges';
import { aiApi, messagesApi, deletionApi } from '../../lib/api';

// Lingue disponibili per traduzione messaggio/risposta.
const LANGS = [
  { value: 'italiano', label: 'Italiano' },
  { value: 'inglese', label: 'Inglese' },
  { value: 'francese', label: 'Francese' },
  { value: 'spagnolo', label: 'Spagnolo' },
  { value: 'tedesco', label: 'Tedesco' },
];

// Mappa codici/nomi restituiti da detect-language sui valori del selettore.
function normalizeLang(raw: string | null | undefined): string {
  const v = (raw ?? '').trim().toLowerCase();
  if (!v) return 'italiano';
  if (v.startsWith('it')) return 'italiano';
  if (v.startsWith('en') || v.includes('ingl')) return 'inglese';
  if (v.startsWith('fr') || v.includes('franc')) return 'francese';
  if (v.startsWith('es') || v.includes('spagn') || v.includes('span')) return 'spagnolo';
  if (v.startsWith('de') || v.includes('tedes') || v.includes('german')) return 'tedesco';
  const match = LANGS.find((l) => l.value === v);
  return match ? match.value : 'italiano';
}

// Estrae testo semplice da un corpo HTML (per traduzione/anteprima).
function htmlToPlainText(html: string): string {
  return html
    .replace(/<style\b[^<]*(?:(?!<\/style>)<[^<]*)*<\/style>/gi, '')
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
    .replace(/<br\s*\/?>(?=)/gi, '\n')
    .replace(/<\/(p|div|li|tr|h[1-6])>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

export function MailViewer() {
  const messageId = useMailStore((s) => s.selectedMessageId);
  const { data: message, isLoading } = useMessage(messageId, true);
  const queryClient = useQueryClient();

  // Stato corrente "Da rispondere" del messaggio (letto in modo difensivo).
  const replyStatus = (message as { reply_status?: 'todo' | 'done' | null } | null)?.reply_status ?? null;

  // Mutation per impostare/azzerare lo stato "Da rispondere".
  const replyStatusMutation = useMutation({
    mutationFn: (status: 'todo' | 'done' | null) =>
      messagesApi.setReplyStatus(messageId as string, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['to-reply'] });
      queryClient.invalidateQueries({ queryKey: ['message'] });
      queryClient.invalidateQueries({ queryKey: ['feed'] });
      queryClient.invalidateQueries({ queryKey: ['messages'] });
    },
  });

  // Toggle: se non è 'todo' → 'todo'; se è 'todo' → null (rimuove il flag).
  // Lo stato 'done' viene impostato dal backend dopo l'invio di una risposta.
  function toggleReplyStatus() {
    if (!messageId) return;
    replyStatusMutation.mutate(replyStatus === 'todo' ? null : 'todo');
  }

  // Stato traduzione del corpo messaggio
  const [translated, setTranslated] = useState<string | null>(null);
  const [showOriginal, setShowOriginal] = useState(false);
  const [translating, setTranslating] = useState(false);
  const [translateLang, setTranslateLang] = useState('italiano');
  const [translateErr, setTranslateErr] = useState<string | null>(null);

  // Composer di risposta ('reply' o 'reply-all'); null = chiuso
  const [replyMode, setReplyMode] = useState<'reply' | 'reply-all' | null>(null);

  // Reset di tutti gli stati quando si cambia messaggio selezionato
  useEffect(() => {
    setTranslated(null);
    setShowOriginal(false);
    setTranslating(false);
    setTranslateErr(null);
    setReplyMode(null);
  }, [messageId]);

  if (!messageId) return <WelcomeState />;
  if (isLoading) {
    return (
      <section className="flex flex-1 items-center justify-center bg-zinc-950">
        <Loader2 size={20} className="animate-spin text-zinc-600" />
      </section>
    );
  }
  if (!message) {
    return (
      <section className="flex flex-1 items-center justify-center bg-zinc-950 text-sm text-zinc-500">
        Messaggio non trovato.
      </section>
    );
  }

  const body = (message as { body?: { text: string | null; html: string | null; error?: string } })
    .body;

  // Corpo in testo semplice usato per traduzione e come originale per la risposta AI
  const bodyText = body?.text ?? (body?.html ? htmlToPlainText(body.html) : '');

  // Traduce il corpo del messaggio nella lingua selezionata
  async function handleTranslate() {
    if (!bodyText.trim() || translating) return;
    setTranslating(true);
    setTranslateErr(null);
    try {
      const res = await aiApi.translate({ text: bodyText, target_lang: translateLang });
      setTranslated(res?.translated ?? '');
      setShowOriginal(false);
    } catch {
      setTranslateErr('Traduzione non riuscita');
    } finally {
      setTranslating(false);
    }
  }

  return (
    <section className="flex flex-1 flex-col overflow-hidden bg-zinc-950">
      {/* Toolbar */}
      <div className="flex items-center justify-between border-b border-zinc-800/80 px-4 py-2">
        <div className="flex items-center gap-1">
          <ToolbarBtn
            icon={Reply}
            label="Rispondi"
            shortcut="R"
            active={replyMode === 'reply'}
            onClick={() => setReplyMode((m) => (m === 'reply' ? null : 'reply'))}
          />
          <ToolbarBtn
            icon={ReplyAll}
            label="Rispondi a tutti"
            shortcut="A"
            active={replyMode === 'reply-all'}
            onClick={() => setReplyMode((m) => (m === 'reply-all' ? null : 'reply-all'))}
          />
          <ToolbarBtn icon={Forward} label="Inoltra" shortcut="F" />
          <Sep />
          <ToolbarBtn icon={Star} label="Stella" shortcut="S" active={message.is_flagged} />
          {/* Cestina = parcheggio 48h: reversibile, poi cancellazione reale dal server */}
          <ToolbarBtn
            icon={Trash2}
            label="Cestina (48h)"
            shortcut="#"
            onClick={async () => {
              await deletionApi.mark([message.id]);
              useMailStore.getState().setSelectedMessage(null);
              queryClient.invalidateQueries({ queryKey: ['deletion-alert'] });
              queryClient.invalidateQueries({ queryKey: ['messages'] });
              queryClient.invalidateQueries({ queryKey: ['feed'] });
            }}
          />
          <Sep />
          {/* Pulsante "Da rispondere" con etichetta ben visibile (non solo icona). */}
          <button
            type="button"
            onClick={toggleReplyStatus}
            title={replyStatus === 'todo' ? 'Rimuovi da rispondere' : 'Segna da rispondere'}
            className={
              'flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-semibold transition ' +
              (replyStatus === 'todo'
                ? 'bg-orange-500 text-white hover:bg-orange-600'
                : 'border border-orange-500/50 text-orange-600 hover:bg-orange-500/10 dark:text-orange-300')
            }
          >
            <Flag size={13} />
            {replyStatus === 'todo' ? 'Da rispondere' : 'Segna da rispondere'}
          </button>
        </div>
        <div className="flex items-center gap-2">
          {/* Traduci messaggio: selettore lingua + azione, con toggle originale/traduzione */}
          <div className="flex items-center gap-1">
            <select
              value={translateLang}
              onChange={(e) => setTranslateLang(e.target.value)}
              title="Lingua di traduzione"
              className="rounded-md border border-zinc-700 bg-zinc-900/60 px-1.5 py-1 text-[11px] text-zinc-300 outline-none transition hover:border-zinc-600"
            >
              {LANGS.map((l) => (
                <option key={l.value} value={l.value}>
                  {l.label}
                </option>
              ))}
            </select>
            {translated ? (
              <button
                onClick={() => setShowOriginal((o) => !o)}
                className="flex items-center gap-1.5 rounded-md border border-zinc-700 bg-zinc-900/60 px-2 py-1 text-[11px] text-zinc-300 transition hover:bg-zinc-800/60"
              >
                <Languages size={11} />
                {showOriginal ? 'Mostra traduzione' : 'Mostra originale'}
              </button>
            ) : (
              <button
                onClick={handleTranslate}
                disabled={translating || !bodyText.trim()}
                className="flex items-center gap-1.5 rounded-md border border-zinc-700 bg-zinc-900/60 px-2 py-1 text-[11px] text-zinc-300 transition hover:bg-zinc-800/60 disabled:opacity-50"
              >
                {translating ? (
                  <Loader2 size={11} className="animate-spin" />
                ) : (
                  <Languages size={11} />
                )}
                Traduci
              </button>
            )}
          </div>
          {message.ai_summary && (
            <button className="flex items-center gap-1.5 rounded-md border border-accent-500/30 bg-accent-500/10 px-2 py-1 text-[11px] text-accent-300 transition hover:bg-accent-500/20">
              <Sparkles size={11} />
              AI summary
            </button>
          )}
        </div>
      </div>

      {/* Header mail */}
      <div className="border-b border-zinc-800/80 px-6 py-4">
        <div className="flex items-start justify-between gap-3">
          <h1 className="text-lg font-semibold tracking-tight text-zinc-100">
            {message.subject ?? '(senza oggetto)'}
          </h1>
          {replyStatus === 'todo' ? (
            <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-orange-100 px-2 py-0.5 text-[10px] font-medium text-orange-700 dark:bg-orange-900/40 dark:text-orange-300">
              <Flag size={10} /> Da rispondere
            </span>
          ) : replyStatus === 'done' ? (
            <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-medium text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">
              <CheckCircle2 size={10} /> Risposta
            </span>
          ) : null}
        </div>
        <BadgeBar
          messageId={messageId}
          tags={(message as { tags?: MessageTag[] }).tags ?? []}
        />
        <TagEditor
          messageId={messageId}
          tags={(message as { tags?: MessageTag[] }).tags ?? []}
        />
        <div className="mt-2 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-br from-zinc-700 to-zinc-800 text-xs font-semibold uppercase">
              {(message.from_name ?? message.from_address ?? '?')[0]}
            </div>
            <div>
              <div className="text-sm font-medium text-zinc-200">
                {message.from_name ?? message.from_address}
              </div>
              <div className="text-[11px] text-zinc-500">
                {message.from_address}
                {' · a: '}
                {(message.to_addresses as Array<{ address: string }>)
                  ?.map((t) => t.address)
                  .join(', ')}
              </div>
            </div>
          </div>
          <div className="text-[11px] text-zinc-500">
            {message.received_at && format(new Date(message.received_at), 'dd MMM yyyy · HH:mm')}
          </div>
        </div>
        {message.ai_summary && (
          <div className="mt-3 rounded-md border border-accent-500/20 bg-accent-500/5 p-3">
            <div className="mb-1 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-accent-400">
              <Sparkles size={10} /> Riassunto AI
            </div>
            <p className="text-xs text-zinc-300">{message.ai_summary}</p>
          </div>
        )}
        {(() => {
          // Entità estratte dall'AI (azione extract_entities) mostrate sotto il riassunto
          const entities = (message as { ai_entities?: Record<string, unknown> }).ai_entities;
          if (!entities || Object.keys(entities).length === 0) return null;
          return (
            <div className="mt-3 rounded-md border border-zinc-800 bg-zinc-950/40 p-3">
              <div className="mb-1.5 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-zinc-500">
                <Sparkles size={10} /> Entità estratte
              </div>
              <div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-xs">
                {Object.entries(entities).map(([k, v]) => (
                  <div key={k} className="contents">
                    <span className="font-mono text-[11px] text-zinc-500">{k}</span>
                    <span className="text-zinc-300">
                      {typeof v === 'object' ? JSON.stringify(v) : String(v)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          );
        })()}
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto px-6 py-5">
        {translateErr && (
          <div className="mb-3 rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-300">
            {translateErr}
          </div>
        )}
        {translated && !showOriginal ? (
          // Corpo tradotto (toggle "Mostra originale" in alto ripristina l'originale)
          <pre className="whitespace-pre-wrap font-sans text-sm leading-relaxed text-zinc-300">
            {translated}
          </pre>
        ) : body?.html ? (
          <div
            className="prose prose-invert prose-sm max-w-none [&_a]:text-accent-400"
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{ __html: sanitizeHtml(body.html) }}
          />
        ) : body?.text ? (
          <pre className="whitespace-pre-wrap font-sans text-sm leading-relaxed text-zinc-300">
            {body.text}
          </pre>
        ) : body?.error ? (
          // Il backend ha risposto ma il corpo non è caricabile: messaggio chiaro
          // invece del caricamento infinito.
          <div className="text-sm text-amber-400/80">
            {body.error === 'unavailable'
              ? 'Il corpo di questo messaggio non è più disponibile sul server di posta.'
              : 'Impossibile caricare il corpo del messaggio. Riprova più tardi.'}
          </div>
        ) : body ? (
          // Body ricevuto ma senza testo/HTML (messaggio realmente vuoto).
          <div className="text-sm text-zinc-500">(Nessun contenuto testuale)</div>
        ) : (
          <div className="text-sm text-zinc-500">Caricamento corpo messaggio…</div>
        )}

        {message.has_attachments && message.attachment_count > 0 && (
          <div className="mt-6 border-t border-zinc-800 pt-4">
            <div className="mb-2 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-zinc-500">
              <Paperclip size={10} /> Allegati ({message.attachment_count})
            </div>
            <p className="text-xs text-zinc-500">
              La conversione in Markdown sarà disponibile nella Fase 3 (worker AI dedicato).
            </p>
          </div>
        )}

        {/* Risposte inviate dall'app a QUESTO messaggio */}
        {messageId && <SentReplies messageId={messageId} />}

        {/* Composer di risposta (appare sotto il messaggio) */}
        {replyMode && (
          <ReplyComposer
            key={replyMode}
            message={message as Record<string, unknown>}
            mode={replyMode}
            originalText={bodyText}
            onClose={() => setReplyMode(null)}
          />
        )}
      </div>
    </section>
  );
}

function WelcomeState() {
  return (
    <section className="flex flex-1 flex-col bg-zinc-950">
      <div className="flex h-full flex-col items-center justify-center px-8 text-center">
        <div className="mb-6 flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-accent-500/20 to-cyan-700/20 ring-1 ring-accent-500/30">
          <Mail size={28} className="text-accent-400" />
        </div>
        <h2 className="text-xl font-semibold tracking-tight">Benvenuto in WebMail AI</h2>
        <p className="mt-2 max-w-md text-sm text-zinc-400">
          Seleziona una mail dalla lista per iniziare. Le mail arrivano in tempo reale via IMAP
          IDLE.
        </p>
        <div className="mt-8 flex items-center gap-2 text-[11px] text-zinc-600">
          <Sparkles size={12} />
          Powered by Netlab · webmail.unikai.it
        </div>
      </div>
    </section>
  );
}

function ToolbarBtn({
  icon: Icon,
  label,
  shortcut,
  active,
  onClick,
}: {
  icon: typeof Sparkles;
  label: string;
  shortcut?: string;
  active?: boolean;
  onClick?: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={shortcut ? `${label} (${shortcut})` : label}
      className={`group flex items-center gap-1 rounded p-1.5 transition ${
        active
          ? 'bg-accent-500/15 text-accent-300'
          : 'text-zinc-500 hover:bg-zinc-800/60 hover:text-zinc-200'
      }`}
    >
      <Icon size={13} />
    </button>
  );
}

function Sep() {
  return <div className="mx-1 h-4 w-px bg-zinc-800" />;
}

// Tag associato a un messaggio (shape allineata al contratto shared)
type MessageTag = { id: string; name: string; color: string; kind?: 'normal' | 'badge' };

/**
 * Barra dei BADGE del messaggio (pallini colorati + etichetta).
 * - Mostra i badge assegnati con "×" per toglierli (elimina anche la copia IMAP).
 * - Il pulsante bandierina apre l'elenco badge disponibili (Alert, Da rispondere
 *   e custom) per assegnarli/toglierli con un toggle.
 */
function BadgeBar({ messageId, tags }: { messageId: string; tags: MessageTag[] }) {
  const { data: badges } = useBadges();
  const addBadge = useAddMessageBadge();
  const removeBadge = useRemoveMessageBadge();
  const [open, setOpen] = useState(false);

  const assigned = tags.filter((t) => t.kind === 'badge');
  const assignedIds = new Set(assigned.map((b) => b.id));
  const busy = addBadge.isPending || removeBadge.isPending;

  return (
    <div className="mt-2 flex flex-wrap items-center gap-1.5">
      {assigned.map((b) => (
        <span
          key={b.id}
          className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium"
          style={{ backgroundColor: `${b.color}22`, color: b.color }}
        >
          <span className="rounded-full" style={{ width: 8, height: 8, backgroundColor: b.color }} />
          {b.name}
          <button
            type="button"
            title={`Togli badge ${b.name}`}
            disabled={busy}
            onClick={() => removeBadge.mutate({ messageId, badgeId: b.id })}
            className="ml-0.5 rounded-full p-0.5 transition hover:bg-black/10 disabled:opacity-50"
            style={{ color: b.color }}
          >
            <X size={9} />
          </button>
        </span>
      ))}

      <div className="relative">
        <button
          type="button"
          title="Badge…"
          disabled={busy}
          onClick={() => setOpen((v) => !v)}
          className="inline-flex items-center gap-1 rounded-full border border-zinc-700 px-2 py-0.5 text-[10px] text-zinc-300 transition hover:bg-zinc-800 disabled:opacity-50"
        >
          <Flag size={10} /> Badge
        </button>
        {open && (
          <ul className="absolute z-20 mt-1 max-h-60 w-52 overflow-y-auto rounded-md border border-zinc-800 bg-zinc-900 py-1 shadow-xl">
            {(badges ?? []).length === 0 && (
              <li className="px-3 py-1 text-[10px] text-zinc-500">Nessun badge disponibile</li>
            )}
            {(badges ?? []).map((b) => {
              const on = assignedIds.has(b.id);
              return (
                <li key={b.id}>
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => {
                      if (on) removeBadge.mutate({ messageId, badgeId: b.id });
                      else addBadge.mutate({ messageId, badge: { badgeId: b.id } });
                      setOpen(false);
                    }}
                    className="flex w-full items-center gap-2 px-3 py-1 text-left text-[11px] text-zinc-200 hover:bg-zinc-800 disabled:opacity-50"
                  >
                    <span
                      className="rounded-full"
                      style={{ width: 10, height: 10, backgroundColor: b.color }}
                    />
                    <span className="flex-1">{b.name}</span>
                    {on && <CheckCircle2 size={12} className="text-emerald-400" />}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}

/**
 * Editor dei tag mostrati all'apertura di una mail.
 * - Ogni tag è un chip con "×" per rimuoverlo.
 * - Il campo "+" permette di aggiungere un tag esistente (autocomplete via useTags)
 *   oppure di crearne uno nuovo digitandone il nome e premendo Invio.
 * Le mutation invalidano le query rilevanti (dettaglio/feed/liste) per aggiornare la UI.
 */
function TagEditor({ messageId, tags: allMsgTags }: { messageId: string; tags: MessageTag[] }) {
  // I badge hanno una barra dedicata: qui mostriamo solo i tag normali.
  const tags = allMsgTags.filter((t) => t.kind !== 'badge');
  const { data: allTags } = useTags();
  const addTag = useAddMessageTag();
  const removeTag = useRemoveMessageTag();

  const [adding, setAdding] = useState(false);
  const [value, setValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (adding) inputRef.current?.focus();
  }, [adding]);

  // Id dei tag già applicati (per escluderli dai suggerimenti)
  const appliedIds = useMemo(() => new Set(tags.map((t) => t.id)), [tags]);

  // Suggerimenti: tag esistenti non ancora applicati, filtrati per testo digitato
  const suggestions = useMemo(() => {
    const q = value.trim().toLowerCase();
    return (allTags ?? [])
      .filter((t) => !appliedIds.has(t.id))
      .filter((t) => (q ? t.name.toLowerCase().includes(q) : true))
      .slice(0, 8);
  }, [allTags, appliedIds, value]);

  // Nome digitato che non corrisponde ad alcun tag esistente → possibile creazione
  const canCreate =
    value.trim().length > 0 &&
    !(allTags ?? []).some((t) => t.name.toLowerCase() === value.trim().toLowerCase());

  const busy = addTag.isPending || removeTag.isPending;

  function handleAddExisting(tagId: string) {
    addTag.mutate({ messageId, tag: { tagId } });
    setValue('');
    setAdding(false);
  }

  function handleCreate() {
    const name = value.trim();
    if (!name) return;
    addTag.mutate({ messageId, tag: { name } });
    setValue('');
    setAdding(false);
  }

  function handleKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') {
      e.preventDefault();
      const exact = (allTags ?? []).find(
        (t) => t.name.toLowerCase() === value.trim().toLowerCase(),
      );
      if (exact) handleAddExisting(exact.id);
      else handleCreate();
    } else if (e.key === 'Escape') {
      setValue('');
      setAdding(false);
    }
  }

  return (
    <div className="mt-2 flex flex-wrap items-center gap-1.5">
      {tags.map((t) => (
        <span
          key={t.id}
          className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium"
          style={{ backgroundColor: `${t.color}22`, color: t.color }}
        >
          <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: t.color }} />
          {t.name}
          <button
            type="button"
            title="Rimuovi tag"
            disabled={busy}
            onClick={() => removeTag.mutate({ messageId, tagId: t.id })}
            className="ml-0.5 rounded-full p-0.5 transition hover:bg-black/10 disabled:opacity-50"
            style={{ color: t.color }}
          >
            <X size={9} />
          </button>
        </span>
      ))}

      {adding ? (
        <div className="relative">
          <div className="flex items-center gap-1 rounded-full border border-zinc-700 bg-zinc-950/60 px-2 py-0.5">
            <input
              ref={inputRef}
              value={value}
              disabled={busy}
              onChange={(e) => setValue(e.target.value)}
              onKeyDown={handleKeyDown}
              onBlur={() => {
                setTimeout(() => setAdding(false), 150);
              }}
              placeholder="Aggiungi tag…"
              className="w-28 bg-transparent text-[10px] text-zinc-200 placeholder-zinc-500 outline-none"
            />
          </div>

          {(suggestions.length > 0 || canCreate) && (
            <ul className="absolute z-10 mt-1 max-h-48 w-44 overflow-y-auto rounded-md border border-zinc-800 bg-zinc-900 py-1 shadow-xl">
              {suggestions.map((t) => (
                <li key={t.id}>
                  <button
                    type="button"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      handleAddExisting(t.id);
                    }}
                    className="flex w-full items-center gap-2 px-2 py-1 text-left text-[11px] text-zinc-300 transition hover:bg-zinc-800/60"
                  >
                    <span
                      className="h-2 w-2 shrink-0 rounded-full"
                      style={{ backgroundColor: t.color }}
                    />
                    <span className="truncate">{t.name}</span>
                  </button>
                </li>
              ))}
              {canCreate && (
                <li>
                  <button
                    type="button"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      handleCreate();
                    }}
                    className="flex w-full items-center gap-2 px-2 py-1 text-left text-[11px] text-accent-300 transition hover:bg-zinc-800/60"
                  >
                    <Plus size={10} />
                    <span className="truncate">Crea "{value.trim()}"</span>
                  </button>
                </li>
              )}
            </ul>
          )}
        </div>
      ) : (
        <button
          type="button"
          disabled={busy}
          onClick={() => setAdding(true)}
          title="Aggiungi tag"
          className="inline-flex items-center gap-1 rounded-full border border-dashed border-zinc-700 px-2 py-0.5 text-[10px] font-medium text-zinc-400 transition hover:border-zinc-600 hover:text-zinc-200 disabled:opacity-50"
        >
          <Plus size={9} /> Tag
        </button>
      )}
    </div>
  );
}

type Addr = { address: string; name?: string };

// Legge in modo difensivo un array di indirizzi ({address,name}) dai campi del messaggio.
function readAddrs(v: unknown): Addr[] {
  if (!Array.isArray(v)) return [];
  return v.filter(
    (a): a is Addr => !!a && typeof (a as { address?: unknown }).address === 'string',
  );
}

// Divide una stringa di destinatari (virgola/punto e virgola) in array di email.
function parseEmails(str: string): string[] {
  return str
    .split(/[,;]+/)
    .map((x) => x.trim())
    .filter(Boolean);
}

/**
 * Composer di RISPOSTA mostrato sotto il messaggio.
 * - Precompila A (mittente originale) e, in reply-all, Cc (altri destinatari) escludendo la propria casella.
 * - Oggetto "Re: ...", corpo modificabile, citazione dell'originale facoltativa.
 * - Invia via messagesApi.send con threading (in_reply_to + references); il backend fa APPEND in Inviata.
 * - "Migliora con AI" e "Traduci risposta" agiscono sulla bozza corrente.
 */
function ReplyComposer({
  message,
  mode,
  originalText,
  onClose,
}: {
  message: Record<string, unknown>;
  mode: 'reply' | 'reply-all';
  originalText: string;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const { data: accounts } = useAccounts();

  const accountId = String(message.account_id ?? '');
  const selfEmail = useMemo(
    () => accounts?.find((a) => a.id === accountId)?.email?.toLowerCase() ?? '',
    [accounts, accountId],
  );

  const fromAddress = (message.from_address as string | null) ?? '';
  const fromName = (message.from_name as string | null) ?? fromAddress;
  const origMessageId = (message.message_id as string | null) ?? null;
  const origReferences = Array.isArray(message.mail_references)
    ? (message.mail_references as string[])
    : [];
  const origSubject = (message.subject as string | null) ?? '';
  const receivedAt = (message.received_at as string | null) ?? null;

  // Destinatari iniziali: A = mittente originale
  const initialTo = useMemo(() => (fromAddress ? [fromAddress] : []), [fromAddress]);

  // Cc iniziale (solo reply-all): to + cc originali, meno se stessi e meno il mittente
  const initialCc = useMemo(() => {
    if (mode !== 'reply-all') return [];
    const all = [...readAddrs(message.to_addresses), ...readAddrs(message.cc_addresses)];
    const seen = new Set<string>();
    const exclude = new Set([selfEmail, fromAddress.toLowerCase()].filter(Boolean));
    const out: string[] = [];
    for (const a of all) {
      const e = a.address.toLowerCase();
      if (!e || exclude.has(e) || seen.has(e)) continue;
      seen.add(e);
      out.push(a.address);
    }
    return out;
  }, [mode, message.to_addresses, message.cc_addresses, selfEmail, fromAddress]);

  const [to, setTo] = useState(initialTo.join(', '));
  const [cc, setCc] = useState(initialCc.join(', '));
  const [subject, setSubject] = useState(
    /^re:/i.test(origSubject) ? origSubject : `Re: ${origSubject || '(senza oggetto)'}`,
  );
  const [draft, setDraft] = useState('');
  const [includeQuote, setIncludeQuote] = useState(true);

  const [replyLang, setReplyLang] = useState('italiano');
  const [sending, setSending] = useState(false);
  const [improving, setImproving] = useState(false);
  const [translating, setTranslating] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Citazione dell'originale (append facoltativo alla bozza in fase di invio)
  const quoted = useMemo(() => {
    const when = receivedAt ? format(new Date(receivedAt), 'dd MMM yyyy · HH:mm') : '';
    const header = `Il ${when}, ${fromName} ha scritto:`;
    const quotedBody = (originalText || '')
      .split('\n')
      .map((l) => `> ${l}`)
      .join('\n');
    return `${header}\n${quotedBody}`;
  }, [receivedAt, fromName, originalText]);

  // Rileva la lingua dell'originale per proporla come default della traduzione risposta
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!originalText.trim()) return;
      try {
        const r = await aiApi.detectLanguage({ text: originalText });
        if (!cancelled && r?.lang) setReplyLang(normalizeLang(r.lang));
      } catch {
        /* fallback: resta 'italiano' */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [originalText]);

  async function handleImprove() {
    if (!draft.trim() || improving) return;
    setImproving(true);
    setError(null);
    try {
      const r = await aiApi.improveReply({ draft, original: originalText });
      setDraft(r?.improved ?? draft);
    } catch {
      setError('Miglioramento AI non riuscito');
    } finally {
      setImproving(false);
    }
  }

  async function handleTranslateReply() {
    if (!draft.trim() || translating) return;
    setTranslating(true);
    setError(null);
    try {
      const r = await aiApi.translate({ text: draft, target_lang: replyLang });
      setDraft(r?.translated ?? draft);
    } catch {
      setError('Traduzione non riuscita');
    } finally {
      setTranslating(false);
    }
  }

  async function handleSend() {
    if (sending) return;
    const toList = parseEmails(to);
    const ccList = parseEmails(cc);
    if (toList.length === 0) {
      setError('Inserisci almeno un destinatario');
      return;
    }
    setSending(true);
    setError(null);
    const text = includeQuote ? `${draft}\n\n${quoted}` : draft;
    try {
      await messagesApi.send({
        account_id: accountId,
        to: toList,
        ...(ccList.length > 0 ? { cc: ccList } : {}),
        subject,
        text,
        ...(origMessageId ? { in_reply_to: origMessageId } : {}),
        references: origMessageId ? [...origReferences, origMessageId] : origReferences,
      });
      setSent(true);
      // Aggiorna liste/feed/dettaglio dopo l'invio
      queryClient.invalidateQueries({ queryKey: ['messages'] });
      queryClient.invalidateQueries({ queryKey: ['feed'] });
      queryClient.invalidateQueries({ queryKey: ['message'] });
      queryClient.invalidateQueries({ queryKey: ['sent-messages'] });
      queryClient.invalidateQueries({ queryKey: ['message-replies'] });
      // Il backend imposta reply_status='done' dopo l'invio: aggiorna la lista "Da rispondere"
      queryClient.invalidateQueries({ queryKey: ['to-reply'] });
      setTimeout(() => onClose(), 1200);
    } catch {
      setError("Invio non riuscito. Riprova.");
    } finally {
      setSending(false);
    }
  }

  const busy = sending || improving || translating;

  if (sent) {
    return (
      <div className="mt-6 rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-300">
        Risposta inviata e salvata in Inviata.
      </div>
    );
  }

  const inputCls =
    'w-full rounded-md border border-zinc-700 bg-zinc-900/60 px-2.5 py-1.5 text-sm text-zinc-200 outline-none transition focus:border-zinc-500';

  return (
    <div className="mt-6 rounded-lg border border-zinc-800 bg-zinc-900/40 p-4">
      <div className="mb-3 flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-wider text-zinc-400">
          {mode === 'reply-all' ? 'Rispondi a tutti' : 'Rispondi'}
        </span>
        <button
          type="button"
          onClick={onClose}
          title="Chiudi"
          className="rounded p-1 text-zinc-500 transition hover:bg-zinc-800/60 hover:text-zinc-200"
        >
          <X size={14} />
        </button>
      </div>

      <div className="space-y-2">
        <label className="flex items-center gap-2">
          <span className="w-14 shrink-0 text-[11px] text-zinc-500">A</span>
          <input value={to} onChange={(e) => setTo(e.target.value)} className={inputCls} />
        </label>
        {mode === 'reply-all' && (
          <label className="flex items-center gap-2">
            <span className="w-14 shrink-0 text-[11px] text-zinc-500">Cc</span>
            <input value={cc} onChange={(e) => setCc(e.target.value)} className={inputCls} />
          </label>
        )}
        <label className="flex items-center gap-2">
          <span className="w-14 shrink-0 text-[11px] text-zinc-500">Oggetto</span>
          <input
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            className={inputCls}
          />
        </label>
      </div>

      <textarea
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        rows={7}
        placeholder="Scrivi la tua risposta…"
        className="mt-3 w-full resize-y rounded-md border border-zinc-700 bg-zinc-900/60 px-3 py-2 text-sm leading-relaxed text-zinc-200 outline-none transition focus:border-zinc-500"
      />

      {/* Citazione originale (facoltativa) */}
      <label className="mt-2 flex items-center gap-2 text-[11px] text-zinc-500">
        <input
          type="checkbox"
          checked={includeQuote}
          onChange={(e) => setIncludeQuote(e.target.checked)}
          className="accent-accent-500"
        />
        Includi citazione del messaggio originale
      </label>
      {includeQuote && (
        <pre className="mt-2 max-h-32 overflow-y-auto whitespace-pre-wrap rounded-md border border-zinc-800 bg-zinc-950/40 p-2 text-[11px] leading-relaxed text-zinc-500">
          {quoted}
        </pre>
      )}

      {error && (
        <div className="mt-3 rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-300">
          {error}
        </div>
      )}

      {/* Azioni */}
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={handleSend}
          disabled={busy}
          className="flex items-center gap-1.5 rounded-md bg-accent-500 px-3 py-1.5 text-xs font-medium text-zinc-950 transition hover:bg-accent-400 disabled:opacity-50"
        >
          {sending ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
          Invia
        </button>

        <button
          type="button"
          onClick={handleImprove}
          disabled={busy || !draft.trim()}
          className="flex items-center gap-1.5 rounded-md border border-zinc-700 bg-zinc-900/60 px-2.5 py-1.5 text-xs text-zinc-300 transition hover:bg-zinc-800/60 disabled:opacity-50"
        >
          {improving ? <Loader2 size={12} className="animate-spin" /> : <Wand2 size={12} />}
          Migliora con AI
        </button>

        <div className="flex items-center gap-1">
          <select
            value={replyLang}
            onChange={(e) => setReplyLang(e.target.value)}
            title="Lingua traduzione risposta"
            className="rounded-md border border-zinc-700 bg-zinc-900/60 px-1.5 py-1.5 text-xs text-zinc-300 outline-none transition hover:border-zinc-600"
          >
            {LANGS.map((l) => (
              <option key={l.value} value={l.value}>
                {l.label}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={handleTranslateReply}
            disabled={busy || !draft.trim()}
            className="flex items-center gap-1.5 rounded-md border border-zinc-700 bg-zinc-900/60 px-2.5 py-1.5 text-xs text-zinc-300 transition hover:bg-zinc-800/60 disabled:opacity-50"
          >
            {translating ? <Loader2 size={12} className="animate-spin" /> : <Languages size={12} />}
            Traduci risposta
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Sanitizer minimale per il body HTML: rimuove script/iframe.
 * Per produzione sostituire con DOMPurify.
 */
function sanitizeHtml(html: string): string {
  return html
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
    .replace(/<iframe\b[^<]*(?:(?!<\/iframe>)<[^<]*)*<\/iframe>/gi, '')
    .replace(/\son\w+="[^"]*"/gi, '')
    .replace(/\son\w+='[^']*'/gi, '');
}


/**
 * Risposte inviate dall'applicazione al messaggio corrente
 * (webmail.sent_messages con in_reply_to = Message-ID dell'originale).
 */
function SentReplies({ messageId }: { messageId: string }) {
  const { data } = useQuery({
    queryKey: ['message-replies', messageId],
    queryFn: () =>
      messagesApi.replies(messageId) as Promise<{
        replies: {
          id: string;
          to_addresses: string[];
          subject: string | null;
          body_text: string | null;
          body_html: string | null;
          sent_at: string;
        }[];
      }>,
  });
  const replies = data?.replies ?? [];
  if (replies.length === 0) return null;
  return (
    <div className="mt-6 border-t border-zinc-800 pt-4">
      <div className="mb-2 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-emerald-400">
        <Reply size={10} /> Risposte inviate ({replies.length})
      </div>
      <div className="space-y-3">
        {replies.map((r) => (
          <div key={r.id} className="rounded-lg border border-zinc-800 bg-zinc-900/60 p-3">
            <div className="mb-1.5 flex flex-wrap items-center gap-2 text-[11px] text-zinc-500">
              <span className="font-medium text-zinc-300">
                {format(new Date(r.sent_at), 'dd/MM/yyyy HH:mm')}
              </span>
              <span>→ {r.to_addresses.join(', ')}</span>
            </div>
            {r.body_html ? (
              <div
                className="prose prose-invert prose-sm max-w-none"
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{ __html: sanitizeHtml(r.body_html) }}
              />
            ) : (
              <pre className="whitespace-pre-wrap font-sans text-[13px] leading-relaxed text-zinc-300">
                {r.body_text ?? '(nessun contenuto)'}
              </pre>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
